import {
	color, geoNaturalEarth1, geoPath, max, scaleLinear, select, zoom
} from 'd3';
import { useEffect } from 'react';
import { feature } from 'topojson';
import WaitingIndicator from '../../../../general-ui/waiting-indicator/waiting-indicator';
import numberWithCommas from '../../utils/numberWithCommas';
import topoJSONdata0 from '../../../../../utils/countries-50m.json';


const hoverColor = "#eec42d";
const staticColor = '#437c90';

interface AudienceMapProps {
	width: number;
	height: number;
	countryMapData?: Array<any>;
	loading?: boolean;
	error?: string | null;
}
interface toRGBAProps {
	r: number;
	g: number;
	b: number;
	opacity: number;
}

interface fromRGBA {
	r: number;
	g: number;
	b: number;
	a: number;
}

function AudienceMap({ width, height, countryMapData, loading, error }: AudienceMapProps): JSX.Element {

	if (error) {
		return <div className="retention-chart-loading">{error}</div>;
	}

	if (loading) {
		return <div className="retention-chart-loading"><WaitingIndicator /></div>;
	}

	if (!countryMapData?.length) {
		return <div className="retention-chart-loading">No data to display</div>;
	}

	useEffect(() => {
		//Get the element to inject everything into
		const svg: any = select('#audience-map-container')
			.html('')// Empty container to prevent diplicate maps
			.attr('width', width)
			.attr('height', height);

		// 
		const pathGenerator = geoPath().projection(
			geoNaturalEarth1()
				.scale(width / Math.PI / 1.5) // Fits most of the world in the view container. Divide with by 2 PI to fit all the way
				.translate([width / 2, height / 1.5]) // Centers horizontally, and moves focus slightly north to hide Antarctica by defualt
		);

		const g = svg.append('g');
		// Allows for the map to be zoomable and pannable
		svg.call(
			zoom().on('zoom', (event) => {
				g.attr('transform', event.transform);
			})
		);


		// Wait for all the data to load
			const topoJSONdata: any = topoJSONdata0;
			const tracts: any = feature(
				topoJSONdata,
				topoJSONdata.objects.countries
			);

			const acsDataByTractId: any = {};
			const acsData = countryMapData;

			acsData.forEach(row => {
				acsDataByTractId[row.isoId] = row;
			});

			tracts.features.forEach((feature: any) => {
				Object.assign(
					feature.properties,
					{
						count: 0, // If no audience, set count to 0
						...acsDataByTractId[feature.id]
					}
				);
			});


			const globalMax: any = max(acsData, (d: any) => d.count);

			// Creates a color scale based on the range 0 to max viewers per region
			const whiteScale: any = scaleLinear<string>()
				.domain([0, globalMax])
				.range([
					'rgba(118, 58, 149, .9)',
					'rgba(217, 156, 247, .9)',
				]);

			const toRGBA: any = ({ r, g, b, opacity }: toRGBAProps) => ({
				r,
				g,
				b,
				a: opacity,
			});

			const fromRGBA = ({ r, g, b, a }: fromRGBA) =>
				`rgba(${r},${g},${b},${a})`;

			const tooltip = select('.d3-tooltip-location')
				.style('position', 'absolute')
				.style('z-index', '10')
				.style('visibility', 'hidden')
				.style('padding', '10px')
				.style('background', 'rgba(0,0,0,0.6)')
				.style('border-radius', '4px')
				.style('color', '#fff')
				.text('a simple tooltip');

			// Creates the polygon that will represent the area shown on the map. 
			g.selectAll('path.polygon')
				.data(tracts.features)
				.enter()
				.append('path')
				.attr('class', 'polygon')
				.attr('id', (d: any) => {
					return d.id;
				})
				.attr('d', pathGenerator)
				.attr('fill', (d: any) => {
					const whiteColor: any = whiteScale(d.properties.count);
					return fromRGBA(toRGBA(color(whiteColor)));
				})
				.on('mouseover', function(d: any, i: any) {
					const stateCount = i.properties.states?.map((state: any) => state.state && state.count ? `<div>${state.state}: ${state.count}</div>` : '').join('') || '';
					tooltip
						.html(
							`<div>${i.properties.name}</div><div>Views: ${numberWithCommas(i.properties.count)}</div>`
							+ `${stateCount}`
						)
						.style('visibility', 'visible');
					select(".d3-tooltip").transition().attr('fill', hoverColor);

				})
				.on('mousemove', function(d: any) {
					tooltip
						.style('top', d.pageY - 10 + 'px')
						.style('left', d.pageX + 10 + 'px');
				})
				.on('mouseout', function() {
					tooltip.html(``).style('visibility', 'hidden');
					select('.d3-tooltip').transition().attr('fill', staticColor);
				});

	}, [height, width, countryMapData]);

	return (
		// Such empty, much injections
		<svg id="audience-map-container" >

		</svg>
	);
}

export default AudienceMap;
